import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  User, 
  Mail, 
  Smartphone, 
  ArrowRight, 
  GraduationCap, 
  CheckCircle,
  AlertCircle,
  Phone,
  MessageCircle
} from 'lucide-react';
import Header from '../common/Header';
import zohoService from '../../services/zohoService';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import "react-phone-number-input/style.css";

const Register = () => {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp } = useAuth();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    classLookingFor: ''
  });
  
  // Phone state for PhoneInput
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [countryName, setCountryName] = useState('');
  
  // OTP management
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  
  // Zoho CRM status
  const [zohoStatus, setZohoStatus] = useState(null); // 'checking', 'exists', 'new', 'error'
  const [zohoMessage, setZohoMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    if (value) {
      const phoneNumber = parsePhoneNumber(value);
      if (phoneNumber) {
        setDialCode(phoneNumber.countryCallingCode);
        setCountryName(phoneNumber.country);
      }
    }
  };

  const validateStep1 = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!phone) {
      setError('WhatsApp number is required');
      return false;
    }
    if (!formData.classLookingFor) {
      setError('Please select the class you are looking for');
      return false;
    }
    return true;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Step 1: Check Zoho CRM for existing number
      setZohoStatus('checking');
      setZohoMessage('Checking if number exists in our system...');
      
      // TODO: Replace with actual Zoho CRM API call
      const zohoResult = await checkZohoCRM(phone);
      
      if (zohoResult.exists) {
        // Number already exists - redirect to login
        setZohoStatus('exists');
        setZohoMessage('This mobile number is already registered. Please login instead.');
        setSuccess('Account already exists! Redirecting to login...');
        
        setTimeout(() => {
          navigate('/login/parent');
        }, 3000);
        return;
      } else {
        // Number is new - proceed to OTP
        setZohoStatus('new');
        setZohoMessage('Mobile number verified. Sending OTP...');
        
        // Extract country code and mobile number separately
        const phoneNumber = parsePhoneNumber(phone);
        if (!phoneNumber) {
          setError('Please enter a valid phone number');
          setLoading(false);
          return;
        }

        const countryCode = phoneNumber.countryCallingCode;
        const mobileNumber = phoneNumber.nationalNumber;

        console.log('📱 Sending OTP with:', { countryCode, mobileNumber, fullPhone: phone });

        // Send OTP
        const otpResult = await sendOtp(countryCode, mobileNumber);
        if (otpResult.success) {
          setOtpSent(true);
          setSuccess('OTP sent successfully to your WhatsApp!');
          setCurrentStep(2);
        } else {
          setError('Failed to send OTP. Please try again.');
        }
      }
    } catch (error) {
      console.error('Step 1 error:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Extract country code and mobile number separately
      const phoneNumber = parsePhoneNumber(phone);
      if (!phoneNumber) {
        setError('Please enter a valid phone number');
        setLoading(false);
        return;
      }

      const countryCode = phoneNumber.countryCallingCode;
      const mobileNumber = phoneNumber.nationalNumber;

      console.log('🔍 Verifying OTP for:', { countryCode, mobileNumber, fullPhone: phone });

      // Verify OTP
      const verifyResult = await verifyOtp(countryCode, mobileNumber, otp);
      
      if (verifyResult.success) {
        setOtpVerified(true);
        setSuccess('OTP verified successfully!');
        
        // Step 3: Create lead in Zoho CRM
        setZohoMessage('Creating your application...');
        
        // TODO: Replace with actual Zoho CRM create lead API call
        const createLeadResult = await createZohoLead({ ...formData, mobile: phone });
        
        if (createLeadResult.success) {
          setSuccess('Account created successfully! Redirecting to login...');
          setTimeout(() => {
            navigate('/login/parent');
          }, 3000);
        } else {
          setError('Account created but failed to sync with CRM. Please contact support.');
        }
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Extract country code and mobile number separately
      const phoneNumber = parsePhoneNumber(phone);
      if (!phoneNumber) {
        setError('Please enter a valid phone number');
        setLoading(false);
        return;
      }

      const countryCode = phoneNumber.countryCallingCode;
      const mobileNumber = phoneNumber.nationalNumber;

      const result = await sendOtp(countryCode, mobileNumber);
      if (result.success) {
        setSuccess('OTP resent successfully!');
      } else {
        setError('Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Zoho CRM integration
  const checkZohoCRM = async (mobile) => {
    try {
      return await zohoService.checkMobileExists(mobile);
    } catch (error) {
      console.error('Zoho CRM check error:', error);
      throw error;
    }
  };

  const createZohoLead = async (userData) => {
    try {
      return await zohoService.createLead(userData);
    } catch (error) {
      console.error('Zoho CRM create lead error:', error);
      throw error;
    }
  };

  const renderStep1 = () => (
    <form onSubmit={handleStep1Submit} className="space-y-6">
      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="form-input pl-12"
              placeholder="First name"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="form-input pl-12"
              placeholder="Last name"
              required
            />
          </div>
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="form-input pl-12"
            placeholder="Enter your email"
            required
          />
        </div>
      </div>

      {/* Mobile Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          WhatsApp Number <span className="text-red-500">*</span>
        </label>
        <PhoneInput
          international
          countryCallingCodeEditable={false}
          defaultCountry="IN"
          value={phone}
          onChange={handlePhoneChange}
          className="form-input"
          placeholder="Enter your WhatsApp number"
        />
        <p className="mt-2 text-sm text-gray-500">
          We'll send an OTP to this number via WhatsApp
        </p>
      </div>

      {/* Class Looking For */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Class Looking For <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <GraduationCap className="h-5 w-5 text-gray-400" />
          </div>
          <select
            name="classLookingFor"
            value={formData.classLookingFor}
            onChange={handleChange}
            className="form-input pl-12 appearance-none bg-white"
            required
          >
            <option value="">Select Class</option>
            <option value="Class 4">Class 4</option>
            <option value="Class 5">Class 5</option>
            <option value="Class 6">Class 6</option>
            <option value="Class 7">Class 7</option>
            <option value="Class 8">Class 8</option>
            <option value="Class 9">Class 9</option>
            <option value="Class 10">Class 10</option>
            <option value="Class 11">Class 11</option>
            <option value="Class 12">Class 12</option>
          </select>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {zohoStatus === 'checking' ? 'Checking...' : 'Processing...'}
          </div>
        ) : (
          <div className="flex items-center">
            Verify Number
            <ArrowRight className="h-4 w-4 ml-2" />
          </div>
        )}
      </button>
    </form>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {/* OTP Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Enter OTP <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MessageCircle className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="form-input pl-12 text-center text-lg tracking-widest"
            placeholder="000000"
            maxLength={6}
            required
          />
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Enter the 6-digit OTP sent to {phone}
        </p>
      </div>

      {/* Resend OTP */}
      <div className="text-center">
        <button
          type="button"
          onClick={resendOtp}
          disabled={loading}
          className="text-sm text-teal-600 hover:text-teal-500 disabled:opacity-50"
        >
          Didn't receive OTP? Resend
        </button>
      </div>

      <button
        onClick={handleOtpSubmit}
        disabled={loading || !otp.trim() || otp.length !== 6}
        className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Verifying OTP...
          </div>
        ) : (
          <div className="flex items-center">
            Verify OTP
            <CheckCircle className="h-4 w-4 ml-2" />
          </div>
        )}
      </button>

      {/* Back to Step 1 */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => setCurrentStep(1)}
          className="text-sm text-gray-600 hover:text-gray-500"
        >
          ← Back to details
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100">
      <Header title="Start Application" showBack={true} backTo="/login" />
      
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Page Title */}
          <div className="text-center">
            <div className="bg-teal-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <GraduationCap className="h-8 w-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Start Application</h2>
            <p className="text-gray-600">
              {currentStep === 1 
                ? 'Create your account to begin the admission process'
                : 'Verify your WhatsApp number to continue'
              }
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-4">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-teal-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 1 ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm font-medium">Details</span>
            </div>
            <div className={`w-8 h-1 ${currentStep >= 2 ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-teal-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 2 ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium">Verify</span>
            </div>
          </div>

          {/* Status Messages */}
          {zohoStatus && (
            <div className={`p-4 rounded-lg border ${
              zohoStatus === 'checking' ? 'bg-blue-50 border-blue-200 text-blue-800' :
              zohoStatus === 'exists' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
              zohoStatus === 'new' ? 'bg-green-50 border-green-200 text-green-800' :
              'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-center">
                {zohoStatus === 'checking' && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>}
                {zohoStatus === 'exists' && <AlertCircle className="h-4 w-4 mr-2" />}
                {zohoStatus === 'new' && <CheckCircle className="h-4 w-4 mr-2" />}
                <span className="text-sm font-medium">{zohoMessage}</span>
              </div>
            </div>
          )}

          {/* Success Messages */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 text-sm font-medium">{success}</span>
              </div>
            </div>
          )}

          {/* Error Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg">
            {currentStep === 1 ? renderStep1() : renderStep2()}
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login/parent')}
                className="font-medium text-teal-600 hover:text-teal-500"
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
